import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import api, { GenericError } from "../api"
import { CreateCustomerRequest, UpdateCustomerRequest } from "../api/customer.api"

export interface CustomersListState {
    customers: any
    loading: boolean
    //TODO: make error interface
    error?: GenericError
    success?: boolean
    countryList: Record<string, any>[]
    fullFreePromoCodes?: Record<string, any>[]
    customersDestinations?: any
    parcelCustomers: Record<string, any>[]
}

const initialState: CustomersListState = {
    customers: {},
    loading: false,
    countryList: [],
    parcelCustomers: []
}

export interface paginationData {
    page?: number
    pageSize?: number
    warehouse?: string

    customerSearch?: {
        plan_type: string
        customer: string
        country: string
        email_verification: string
        date: string
    }
}

export const loadCustomers = createAsyncThunk(
    "customers/list",
    async ({ warehouse }: { warehouse?: string }, thunkAPI) => {
        try {
            const response = await api.customer.customersList({ warehouse })

            return response
        } catch (error: any) {
            console.log(error)
        }
    }
)

export const loadPaginatedCustomers = createAsyncThunk(
    "customers/list-paginated",
    async (data: paginationData, thunkAPI) => {
        try {
            const response = await api.customer.paginatedCustomersList(data)

            return response
        } catch (error: any) {
            console.log(error)
        }
    }
)

export const loadCreditCustomers = createAsyncThunk(
    "customers/list-credit",
    async ({ warehouse }: { warehouse?: string }, thunkAPI) => {
        try {
            const response = await api.customer.creditCustomersList({
                warehouse
            })

            return response
        } catch (error: any) {
            console.log(error)
        }
    }
)

export const createCustomer = createAsyncThunk(
    "customers/create",
    async (newCustomerData: CreateCustomerRequest, thunkAPI) => {
        try {
            const response = await api.customer.createCustomer(newCustomerData)

            return response
        } catch (error: any) {
            console.log(error)
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const updateCustomer = createAsyncThunk(
    "customers/update",
    async (existingCustomerData: UpdateCustomerRequest, thunkAPI) => {
        try {
            const response = await api.customer.updateCustomer(existingCustomerData)

            return response
        } catch (error: any) {
            console.log(error)
            return thunkAPI.rejectWithValue(error)
        }
    }
)
export const loadCountry = createAsyncThunk("customer/country-list", async (thunkAPI) => {
    try {
        const response = await api.customer.loadCountry()

        return response
    } catch (error: any) {
        console.log(error)
    }
})

export const listFullFreePromoCodes = createAsyncThunk(
    "customer/full-free-promo-codes-list",
    async (thunkAPI) => {
        try {
            const response = await api.customer.listFullFreePromoCodes()

            return response
        } catch (error: any) {
            console.log(error)
        }
    }
)

export const loadCustomersDestinations = createAsyncThunk("customers/load-destinations", async (thunkAPI) => {
    try {
        const response = await api.customer.loadCustomersDestinations()

        return response
    } catch (error: any) {
        console.log(error)
    }
})

export const customersSlice = createSlice({
    name: "customers",
    initialState,

    reducers: {
        clearError: (state: CustomersListState) => {
            state.error = undefined
        },
        resetSuccess: (state: CustomersListState) => {
            state.success = undefined
        }
    },

    extraReducers: {
        [loadCustomers.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadCustomers.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.data?.error) {
                state.error = action.payload?.data?.error
            } else {
                state.error = action.payload?.data?.payload?.data || []
            }
        },
        [loadCustomers.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.parcelCustomers =
                action.payload?.data?.payload?.data?.map((c: Record<string, any>) => {
                    return {
                        value: c.user_pin,
                        label: c?.user_pin + " " + c?.name
                    }
                }) || []
        },

        [loadCreditCustomers.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadCreditCustomers.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.data?.error) {
                state.error = action.payload?.data?.error
            } else {
                state.error = action.payload?.data?.payload?.data || []
            }
        },
        [loadCreditCustomers.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.customers = action.payload?.data?.payload?.data || []
        },

        [createCustomer.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [createCustomer.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.data?.error) {
                state.error = action.payload?.data?.error
            } else {
                state.error = action.payload?.data?.payload?.data
            }
        },
        [createCustomer.fulfilled.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.data?.error) {
                state.error = action.payload?.data?.error
            } else {
                state.error = action.payload?.data?.payload?.data || []
            }
            // state.customers = action.payload.data.payload.data
        },

        [updateCustomer.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [updateCustomer.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.data?.error) {
                state.error = action.payload?.data?.error
            } else {
                state.error = action.payload?.data?.payload?.data
            }
        },
        [updateCustomer.fulfilled.toString()]: (state, action) => {
            state.loading = false
            // state.customers = action.payload.data.payload.data
        },

        [loadCountry.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadCountry.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.data?.error || ""
        },
        [loadCountry.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.countryList = action.payload?.data?.payload?.data || []
        },

        [listFullFreePromoCodes.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [listFullFreePromoCodes.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.data?.error || ""
        },
        [listFullFreePromoCodes.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.fullFreePromoCodes = action.payload?.data?.payload?.data || []
        },
        [loadPaginatedCustomers.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadPaginatedCustomers.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.data?.error) {
                state.error = action.payload?.data?.error
            } else {
                state.error = action.payload?.data?.payload || []
            }
        },
        [loadPaginatedCustomers.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.customers = action.payload?.data?.payload || []
        },
        [loadCustomersDestinations.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadCustomersDestinations.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.error || ""
        },
        [loadCustomersDestinations.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.customersDestinations = action.payload?.data?.payload || []
        }
    }
})

export const { clearError, resetSuccess } = customersSlice.actions

export default customersSlice.reducer
